/* Kopstorm promo popup. Opens between data-start and data-end, counts down to data-end, copies the code on click
   and optionally applies it to the cart through /discount/CODE. After closing, an optional pill reopens it.

   28 Oct 2026 (BFCM): optional call to action and sign up form (Shopify customer or Klaviyo list), an audience
   for new visitors only, and a small queue so only one popup opens per page view when several are live
   (highest data-priority first, then the order in the overlay group). */
(() => {
  if (customElements.get('kop-promo-popup')) return;

  const pad = (n) => String(n).padStart(2, '0');
  const TWELVE_HOURS = 43200000;
  const KLAVIYO_REVISION = '2024-10-15';
  const SUBSCRIBED_KEY = 'kop-pp:subscribed';
  const KNOWN_KEY = 'kop-pp:known';
  const PENDING_KEY = 'kop-pp:pending';

  const read = (key) => {
    try {
      return sessionStorage.getItem(key) || localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };

  const write = (store, key, value) => {
    try {
      window[store].setItem(key, value);
    } catch (e) { /* storage blocked */ }
  };

  const drop = (store, key) => {
    try {
      window[store].removeItem(key);
    } catch (e) { /* storage blocked */ }
  };

  /* Parses the ISO string Liquid printed. If a translated or hand typed value slipped through, the digits are
     read as YYYY MM DD HH MM in the shop offset, so a stray character never breaks the schedule. */
  const parseMoment = (value, offset) => {
    if (!value) return NaN;
    const parsed = Date.parse(value);
    if (!isNaN(parsed)) return parsed;
    const digits = String(value).replace(/[^0-9]/g, '');
    if (digits.length < 12) return NaN;
    const iso = `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}T${digits.slice(8, 10)}:${digits.slice(10, 12)}:00${offset || ''}`;
    return Date.parse(iso);
  };

  const findKlaviyoKey = () => {
    const scripts = document.querySelectorAll('script[src*="klaviyo.com"]');
    for (const script of scripts) {
      const src = script.getAttribute('src') || '';
      const byParam = src.match(/company_id=([A-Za-z0-9]+)/);
      if (byParam) return byParam[1];
      const byPath = src.match(/onsite\/js\/([A-Za-z0-9]{5,8})\//);
      if (byPath) return byPath[1];
    }
    return '';
  };

  const klaviyoCookieKnown = () => {
    const match = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
    if (!match) return false;
    try {
      const data = JSON.parse(atob(decodeURIComponent(match[1])));
      return Boolean(data && (data.$exchange_id || data.$email || data.email || data.$phone_number));
    } catch (e) {
      return false;
    }
  };

  /* A visitor counts as known when they are logged in, arrive from a Klaviyo email (_kx), are identified by
     Klaviyo in this browser, or signed up through one of these popups before. */
  let knownPromise = null;
  const isKnownVisitor = () => {
    if (knownPromise) return knownPromise;
    knownPromise = (async () => {
      if (read(SUBSCRIBED_KEY) === '1' || read(KNOWN_KEY) === '1') return true;
      if (new URLSearchParams(window.location.search).has('_kx')) {
        write('localStorage', KNOWN_KEY, '1');
        return true;
      }
      const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (meta && meta.page && meta.page.customerId) return true;
      if (klaviyoCookieKnown()) return true;
      const klaviyo = window.klaviyo;
      if (klaviyo && typeof klaviyo.isIdentified === 'function') {
        try {
          const timeout = new Promise((resolve) => setTimeout(() => resolve(false), 2500));
          if (await Promise.race([klaviyo.isIdentified(), timeout])) return true;
        } catch (e) { /* treat as unknown */ }
      }
      return false;
    })();
    return knownPromise;
  };

  /* One popup per page view. Every popup registers itself; once all of them know whether they may show,
     the running popup with the highest priority opens. The others wait for a next page view. */
  const queue = {
    entries: [],
    pending: false,
    claimed: false,

    add(popup) {
      this.entries.push(popup);
      if (this.pending) return;
      this.pending = true;
      setTimeout(() => this.decide(), 0);
    },

    async decide() {
      this.pending = false;
      const entries = this.entries.splice(0);
      await Promise.all(entries.map((popup) => popup.ready));
      const live = entries.filter((popup) => popup.isConnected && popup.eligible);
      if (!live.length) return;

      const byPriority = (a, b) => b.priority - a.priority;
      const forced = live.find((popup) => popup.forceOpen);
      if (forced) {
        this.claimed = true;
        forced.schedule(0);
        return;
      }

      if (!this.claimed) {
        const running = live.filter((popup) => popup.wait === 0 && !popup.dismissed()).sort(byPriority);
        const upcoming = live.filter((popup) => popup.wait > 0 && !popup.dismissed()).sort((a, b) => a.wait - b.wait || b.priority - a.priority);
        const next = running[0] || upcoming[0];
        if (next) {
          this.claimed = true;
          next.schedule();
          return;
        }
      }

      const withPill = live.filter((popup) => popup.pill && popup.dismissed()).sort(byPriority)[0];
      if (withPill) withPill.schedulePill();
    }
  };

  class KopPromoPopup extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-kop-pp-panel]');
      this.pill = this.hasAttribute('data-pill') ? this.querySelector('[data-kop-pp-pill]') : null;
      this.codeBtn = this.querySelector('[data-kop-pp-copy]');
      this.code = this.dataset.code || '';
      this.start = this.dataset.start ? parseMoment(this.dataset.start, this.dataset.offset) : NaN;
      this.end = parseMoment(this.dataset.end, this.dataset.offset);
      this.editor = this.hasAttribute('data-editor');
      this.priority = parseInt(this.dataset.priority || '1', 10) || 1;
      this.key = 'kop-pp:' + (this.code || this.dataset.campaign || '') + ':' + (this.dataset.end || '');
      this.hint = this.querySelector('[data-kop-pp-hint]');
      this.hintText = this.hint ? this.hint.textContent : '';
      this.units = {};
      this.last = {};
      this.querySelectorAll('[data-kop-pp]').forEach((el) => { this.units[el.dataset.kopPp] = el; });
      this.daysWrap = this.querySelector('[data-kop-pp-days-wrap]');

      this.querySelector('[data-kop-pp-close]')?.addEventListener('click', () => this.close(true));
      this.codeBtn?.addEventListener('click', () => this.copy());
      this.pill?.addEventListener('click', () => this.open());
      this.onKey = (e) => { if (e.key === 'Escape' && this.isOpen) this.close(true); };
      document.addEventListener('keydown', this.onKey);
      this.bindForm();

      if (this.editor) {
        /* Theme editor: a popup opens when its section or one of its blocks is selected in the sidebar and
           closes when anything else is selected, so only one popup shows at a time. The section id comes from
           the event detail, because translation blocks have no element of their own to dispatch on. */
        const section = this.closest('.shopify-section');
        const sectionId = section ? section.id.replace(/^shopify-section-/, '') : '';
        const matches = (event) => Boolean(event.detail && event.detail.sectionId === sectionId);
        this.onEditorSelect = (event) => {
          if (matches(event)) this.open();
          else if (this.isOpen) this.close(false);
        };
        this.onEditorDeselect = (event) => { if (matches(event)) this.close(false); };
        this.onEditorLoad = (event) => { if (matches(event)) this.open(); };
        document.addEventListener('shopify:section:select', this.onEditorSelect);
        document.addEventListener('shopify:block:select', this.onEditorSelect);
        document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
        document.addEventListener('shopify:section:load', this.onEditorLoad);
        if (document.querySelectorAll('kop-promo-popup[data-editor]').length === 1) this.open();
        return;
      }

      const now = Date.now();
      if (isNaN(this.end) || now >= this.end) return this.remove();

      const running = isNaN(this.start) || this.start <= now;
      this.wait = running ? 0 : this.start - now;
      if (this.wait > TWELVE_HOURS) return;

      this.eligible = false;
      this.ready = this.checkEligibility().then((eligible) => { this.eligible = eligible; });
      queue.add(this);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.pillTimer);
      clearInterval(this.interval);
      document.removeEventListener('keydown', this.onKey);
      if (this.onEditorSelect) {
        document.removeEventListener('shopify:section:select', this.onEditorSelect);
        document.removeEventListener('shopify:block:select', this.onEditorSelect);
        document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
        document.removeEventListener('shopify:section:load', this.onEditorLoad);
      }
    }

    get isOpen() {
      return this.panel.classList.contains('is-open');
    }

    async checkEligibility() {
      const params = new URLSearchParams(window.location.search);
      if (read(PENDING_KEY) === this.key) {
        if (params.get('customer_posted') === 'true') {
          this.forceOpen = true;
          this.subscribedOnLoad = true;
          return true;
        }
        if (this.querySelector('[data-kop-pp-form-error]')) {
          this.forceOpen = true;
          return true;
        }
      }
      if (this.dataset.audience === 'new' && await isKnownVisitor()) return false;
      return true;
    }

    dismissed() {
      return read(this.key) === '1';
    }

    schedule(wait) {
      const delay = Math.max(0, parseInt(this.dataset.delay || '0', 10)) * 1000;
      const total = wait === 0 ? 0 : this.wait + delay;
      this.timer = setTimeout(() => {
        if (this.subscribedOnLoad) this.showSuccess();
        this.open();
      }, total);
    }

    schedulePill() {
      this.timer = setTimeout(() => this.showPill(), this.wait || 0);
    }

    reveal(el) {
      if (!el) return;
      el.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-open')));
    }

    conceal(el) {
      if (!el) return;
      el.classList.remove('is-open');
      setTimeout(() => { if (!el.classList.contains('is-open')) el.hidden = true; }, 450);
    }

    open() {
      if (this.isOpen) return;
      if (this.editor) {
        document.querySelectorAll('kop-promo-popup').forEach((popup) => {
          if (popup !== this && popup.isOpen) popup.close(false);
        });
      }
      this.last = {};
      this.tick();
      clearInterval(this.interval);
      this.interval = setInterval(() => this.tick(), 1000);
      this.conceal(this.pill);
      this.reveal(this.panel);
    }

    close(remember) {
      clearInterval(this.interval);
      this.conceal(this.panel);
      if (!remember || this.editor) return;
      const store = this.dataset.reshow === 'campaign' || this.subscribed ? 'localStorage' : 'sessionStorage';
      write(store, this.key, '1');
      if (this.subscribed) return;
      setTimeout(() => this.showPill(), 350);
    }

    showPill() {
      if (!this.pill || this.editor || Date.now() >= this.end) return;
      this.reveal(this.pill);
      clearTimeout(this.pillTimer);
      this.pillTimer = setTimeout(() => { if (Date.now() >= this.end) this.remove(); }, Math.min(this.end - Date.now(), 2147483000));
    }

    tick() {
      let left = Math.max(0, Math.floor(((isNaN(this.end) ? Date.now() : this.end) - Date.now()) / 1000));
      if (left <= 0 && !this.editor) {
        this.close(false);
        this.remove();
        return;
      }
      const days = Math.floor(left / 86400); left -= days * 86400;
      const hours = Math.floor(left / 3600); left -= hours * 3600;
      const minutes = Math.floor(left / 60);
      const seconds = left - minutes * 60;
      if (this.daysWrap) this.daysWrap.hidden = days === 0;
      this.set('days', days);
      this.set('hours', hours);
      this.set('minutes', minutes);
      this.set('seconds', seconds);
    }

    set(unit, value) {
      const el = this.units[unit];
      if (!el) return;
      const text = pad(value);
      if (this.last[unit] === text) return;
      const animate = this.last[unit] !== undefined;
      this.last[unit] = text;
      el.textContent = text;
      if (!animate) return;
      el.classList.remove('is-tick');
      void el.offsetWidth;
      el.classList.add('is-tick');
    }

    bindForm() {
      this.fields = this.querySelector('[data-kop-pp-fields]');
      if (!this.fields) return;
      const form = this.fields.closest('form');
      if (!form) return;

      form.addEventListener('submit', async (event) => {
        if (this.editor) {
          event.preventDefault();
          return;
        }
        write('sessionStorage', PENDING_KEY, this.key);

        if (this.fields.dataset.mode !== 'klaviyo_list') return; /* native Shopify customer post */
        const key = findKlaviyoKey();
        const list = (this.fields.dataset.list || '').replace(/[^A-Za-z0-9]/g, '');
        if (!key || !list) return; /* falls back to the Shopify customer post */

        event.preventDefault();
        const input = form.querySelector('[name="contact[email]"]');
        const email = input ? input.value.trim() : '';
        const error = this.querySelector('[data-kop-pp-error]');
        if (error) error.hidden = true;
        form.classList.add('is-loading');

        try {
          const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(key)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: this.fields.dataset.source || 'Popup',
                  profile: { data: { type: 'profile', attributes: { email } } }
                },
                relationships: { list: { data: { type: 'list', id: list } } }
              }
            })
          });
          if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
          drop('sessionStorage', PENDING_KEY);
          this.showSuccess();
        } catch (err) {
          if (error) error.hidden = false;
        } finally {
          form.classList.remove('is-loading');
        }
      });
    }

    showSuccess() {
      this.subscribed = true;
      drop('sessionStorage', PENDING_KEY);
      write('localStorage', SUBSCRIBED_KEY, '1');
      write('localStorage', this.key, '1');
      const form = this.fields ? this.fields.closest('form') : null;
      if (form) form.hidden = true;
      const success = this.querySelector('[data-kop-pp-success]');
      if (success) success.hidden = false;
      document.dispatchEvent(new CustomEvent('kop:popup:subscribed', { detail: { campaign: this.dataset.campaign } }));
    }

    async copy() {
      if (!this.code) return;
      let copied = false;
      try {
        await navigator.clipboard.writeText(this.code);
        copied = true;
      } catch (e) {
        const area = document.createElement('textarea');
        area.value = this.code;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        try { copied = document.execCommand('copy'); } catch (err) { copied = false; }
        area.remove();
      }

      if (this.hasAttribute('data-apply') && !this.editor && !this.applied) {
        this.applied = true;
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(root + 'discount/' + encodeURIComponent(this.code), { credentials: 'same-origin' }).catch(() => {});
      }

      if (!copied) return;
      this.codeBtn.classList.add('is-copied');
      if (this.hint) this.hint.textContent = this.hint.dataset.copied || this.hintText;
      clearTimeout(this.hintTimer);
      this.hintTimer = setTimeout(() => {
        this.codeBtn.classList.remove('is-copied');
        if (this.hint) this.hint.textContent = this.hintText;
      }, 2200);
    }
  }

  customElements.define('kop-promo-popup', KopPromoPopup);
})();
